"use client";

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { ExpenseDashboard } from "@/lib/api-client";
import { useAppStore, currentView } from "@/lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DollarSign,
  Wallet,
  Receipt,
  Users,
  TrendingUp,
  PieChart,
  CalendarDays,
} from "lucide-react";
import { isStaffRole, formatPrice, formatDate  } from "@/components/booking/format";
import { AvailabilityCalendar } from "@/components/booking/availability-calendar";
import { useI18n } from "@/lib/i18n/provider";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Shared chart palette (reads well in light and dark mode).
const PIE_COLORS = [
  "#0ea5e9",
  "#14b8a6",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#f43f5e",
  "#6366f1",
  "#84cc16",
  "#06b6d4",
  "#a855f7",
];

export function DashboardView() {
  const user = useAppStore((s) => s.user);
  const { t, locale } = useI18n();
  // House filter driving every list and chart ("all" = every house in scope).
  const [houseId, setHouseId] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", houseId],
    queryFn: () =>
      apiGet<ExpenseDashboard>(
        "/api/admin/dashboard" + (houseId !== "all" ? `?productId=${houseId}` : "")
      ),
    enabled: !!user && (isStaffRole(user.role)),
    refetchInterval: 20000,
  });

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-6 h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const { summary, payments, byPayer, byCategory, pendingExpenses, occupancy, byAssignee, byHouseTotals, byCategoryBilled, houses, scope } = data;
  const maxPayerTotal = Math.max(1, ...byPayer.map((p) => p.total));
  const maxCategoryTotal = Math.max(1, ...byCategory.map((c) => c.total));

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <PieChart className="h-6 w-6 text-violet-600" /> {t("dash.title")}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {scope?.productTitle
              ? `${t("dash.subtitle")} · ${scope.productTitle}`
              : t("dash.subtitle")}
          </p>
        </div>
        {/* House filter: drives every figure and chart below */}
        <div className="grid gap-1.5">
          <Label className="text-xs text-muted-foreground">{t("dash.filterHouse")}</Label>
          <Select value={houseId} onValueChange={setHouseId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.allHouses")}</SelectItem>
              {(houses ?? []).map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          icon={DollarSign}
          label={t("dash.summaryTotalPaid")}
          value={formatPrice(summary.totalPaid)}
          color="text-emerald-600 bg-emerald-50"
        />
        <SummaryCard
          icon={Wallet}
          label={t("dash.summaryTotalPending")}
          value={formatPrice(summary.totalPending)}
          color="text-amber-600 bg-amber-50"
        />
        <SummaryCard
          icon={Receipt}
          label={t("dash.summaryPayments")}
          value={String(summary.paymentsCount)}
          color="text-sky-600 bg-sky-50"
        />
        <SummaryCard
          icon={Users}
          label={t("dash.summaryPendingUsers")}
          value={String(summary.pendingUsersCount)}
          color="text-violet-600 bg-violet-50"
        />
      </div>

      {/* Availability & booking calendar */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4" /> {t("calendar.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AvailabilityCalendar />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Occupancy pies: nights per house (all houses) or per member */}
        {!scope?.productId ? (
          <OccupancyCard
            title={t("dash.byHouse")}
            empty={t("dash.noOccupancy")}
            data={(occupancy?.byHouse ?? []).map((h) => ({
              name: h.title,
              value: h.nights,
              extra: "",
            }))}
            unit={t("dash.totalNights")}
            total={occupancy?.totalNights ?? 0}
          />
        ) : (
          <OccupancyCard
            title={t("dash.byMember")}
            empty={t("dash.noOccupancy")}
            data={(occupancy?.byUser ?? []).map((u) => ({
              name: u.name || "-",
              value: u.nights,
              extra: formatPrice(u.spend),
            }))}
            unit={t("dash.totalNights")}
            total={occupancy?.totalNights ?? 0}
          />
        )}
        {!scope?.productId ? (
          <OccupancyCard
            title={t("dash.byMember")}
            empty={t("dash.noOccupancy")}
            data={(occupancy?.byUser ?? []).map((u) => ({
              name: u.name || "-",
              value: u.nights,
              extra: formatPrice(u.spend),
            }))}
            unit={t("dash.totalNights")}
            total={occupancy?.totalNights ?? 0}
          />
        ) : (
          <OccupancyCard
            title={t("dash.expensesByCategory")}
            empty={t("dash.noExpenses")}
            money
            data={(byCategoryBilled ?? []).map((c) => ({
              name: t(`expenses.cat.${c.category}`),
              value: Math.round(c.total * 100) / 100,
              extra: `${c.count}`,
            }))}
            unit={t("dash.totalBilled")}
            total={
              Math.round((byCategoryBilled ?? []).reduce((s, c) => s + c.total, 0) * 100) / 100
            }
          />
        )}
      </div>

      {/* Money pies: per house, and per member (paid / responsible) */}
      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {!scope?.productId && (
          <OccupancyCard
            title={t("dash.expensesPerHouse")}
            empty={t("dash.noExpenses")}
            money
            data={(byHouseTotals ?? []).map((h) => ({
              name: h.title,
              value: Math.round(h.total * 100) / 100,
              extra: `${h.count}`,
            }))}
            unit={t("dash.totalBilled")}
            total={Math.round((byHouseTotals ?? []).reduce((s, h) => s + h.total, 0) * 100) / 100}
          />
        )}
        <OccupancyCard
          title={t("dash.paidPerMember")}
          empty={t("dash.noPayments")}
          money
          data={(byPayer ?? []).map((p) => ({
            name: p.name,
            value: Math.round(p.total * 100) / 100,
            extra: `${p.count}`,
          }))}
          unit={t("dash.summaryTotalPaid")}
          total={Math.round((byPayer ?? []).reduce((s, p) => s + p.total, 0) * 100) / 100}
        />
        <OccupancyCard
          title={t("dash.owedPerMember")}
          empty={t("dash.noExpenses")}
          money
          data={(byAssignee ?? []).map((a) => ({
            name: a.id === "unassigned" ? t("expenses.unassigned") : a.name,
            value: Math.round(a.total * 100) / 100,
            extra: `${a.count}`,
          }))}
          unit={t("dash.totalBilled")}
          total={Math.round((byAssignee ?? []).reduce((s, a) => s + a.total, 0) * 100) / 100}
        />
      </div>

      {/* Share of the stay costs per member (the split of private stays) */}
      {(occupancy?.byUser?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4" /> {t("dash.memberSpend")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="mb-2 text-xs text-muted-foreground">{t("dash.spendHint")}</p>
            {[...(occupancy?.byUser ?? [])]
              .sort((a, b) => b.spend - a.spend)
              .map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm"
                >
                  <span className="font-medium">{u.name || "-"}</span>
                  <span className="flex items-center gap-3 text-muted-foreground">
                    <span>
                      {u.nights} {t("calendar.daySuffix")}
                    </span>
                    <span className="font-medium text-foreground">{formatPrice(u.spend)}</span>
                  </span>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* By payer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4" /> {t("dash.byPayer")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byPayer.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dash.noContributions")}</p>
            ) : (
              byPayer.map((p) => (
                <div key={p.name}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">
                      {formatPrice(p.total)} · {p.count} {p.count === 1 ? "payment" : "payments"}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-emerald-500"
                      style={{ width: `${(p.total / maxPayerTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* By category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="h-4 w-4" /> {t("dash.byCategory")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byCategory.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dash.noContributions")}</p>
            ) : (
              byCategory.map((c) => (
                <div key={c.category}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{t(`expenses.cat.${c.category}`)}</span>
                    <span className="text-muted-foreground">
                      {formatPrice(c.total)} · {c.count}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-violet-500"
                      style={{ width: `${(c.total / maxCategoryTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent payments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("dash.recentPayments")}</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("dash.noPayments")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">{t("expenses.paidDate")}</th>
                    <th className="pb-2 pr-4">{t("expenses.paidBy")}</th>
                    <th className="pb-2 pr-4">{t("expenses.category")}</th>
                    <th className="pb-2 pr-4">{t("expenses.description")}</th>
                    <th className="pb-2 pr-4">{t("expenses.paymentMethod")}</th>
                    <th className="pb-2 text-right">{t("expenses.amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-muted-foreground">
                        {p.paidDate ? formatDate(p.paidDate, locale) : "—"}
                      </td>
                      <td className="py-2 pr-4 font-medium">{p.paidBy?.name ?? "—"}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{t(`expenses.cat.${p.category}`)}</Badge>
                      </td>
                      <td className="py-2 pr-4 max-w-[200px] truncate" title={p.description}>
                        {p.description}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {p.paymentMethod ? t(`expenses.method.${p.paymentMethod}`) : "—"}
                      </td>
                      <td className="py-2 text-right font-medium">{formatPrice(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending expenses */}
      {pendingExpenses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("dash.pendingExpenses")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingExpenses.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm"
              >
                <div>
                  <Badge variant="outline">{t(`expenses.cat.${e.category}`)}</Badge>
                  <span className="ml-2">{e.description}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{e.productTitle}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {t("expenses.dueDate")} {formatDate(e.dueDate, locale)}
                  </span>
                  <span className="font-medium">{formatPrice(e.amount)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function OccupancyCard({
  title,
  empty,
  data,
  unit,
  total,
  money = false,
}: {
  title: string;
  empty: string;
  data: { name: string; value: number; extra: string }[];
  unit: string;
  total: number;
  money?: boolean;
}) {
  const rows = data.filter((d) => d.value > 0);
  const fmt = (n: number) => (money ? formatPrice(n) : String(n));
  // Share of the whole, shown on hover (e.g. "42.3%").
  const sum = rows.reduce((s, r) => s + (r.value || 0), 0);
  const pctOf = (v: number) => {
    if (!sum) return "0%";
    const p = Math.round((v / sum) * 1000) / 10;
    return (Number.isInteger(p) ? String(p) : p.toFixed(1)) + "%";
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PieChart className="h-4 w-4" /> {title}
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {fmt(total)} {unit.toLowerCase()}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={78}
                  paddingAngle={2}
                  label={false}
                >
                  {rows.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any, _n: any, item: any) => {
                    const v = Number(value) || 0;
                    const parts = [money ? formatPrice(v) : String(v), pctOf(v)];
                    if (item?.payload?.extra) parts.push(`(${item.payload.extra})`);
                    return [parts.join(" \u00b7 "), item?.payload?.name ?? ""];
                  }}
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={44}
                  formatter={(_value, _e, i) => (
                    <span className="text-xs text-foreground">
                      {rows[i]?.name} ({fmt(rows[i]?.value ?? 0)} · {pctOf(rows[i]?.value ?? 0)})
                    </span>
                  )}
                />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
